import { DropdownMenu } from '@artosera/ui';
import { ArrowDown, ArrowDownUp, ArrowUp, Check } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { FiltresOeuvres } from '../../api/oeuvres.js';
import styles from './BarreOeuvres.module.css';

type Tri = NonNullable<FiltresOeuvres['tri']>;
type ChampTri = 'date' | 'titre' | 'artiste' | 'inventaire' | 'prix';

/**
 * L'API connaît sept tris, chacun avec son sens : la date et le prix en ont
 * deux, titre, artiste et numéro d'inventaire un seul (croissant).
 */
const CHAMPS: Record<ChampTri, { croissant: Tri; decroissant: Tri | null }> = {
  date: { croissant: 'ancien', decroissant: 'recent' },
  titre: { croissant: 'titre', decroissant: null },
  artiste: { croissant: 'artiste', decroissant: null },
  inventaire: { croissant: 'inventaire', decroissant: null },
  prix: { croissant: 'prix_croissant', decroissant: 'prix_decroissant' },
};
const ORDRE_CHAMPS: readonly ChampTri[] = ['date', 'titre', 'artiste', 'inventaire', 'prix'];

function decomposer(tri: Tri): { champ: ChampTri; sens: 'croissant' | 'decroissant' } {
  for (const champ of ORDRE_CHAMPS) {
    if (CHAMPS[champ].croissant === tri) return { champ, sens: 'croissant' };
    if (CHAMPS[champ].decroissant === tri) return { champ, sens: 'decroissant' };
  }
  return { champ: 'date', sens: 'decroissant' };
}

/** Tri : un menu du critère, et un bouton pour le sens quand le critère en a deux. */
export function MenuTri({
  tri: triCourant,
  onChange,
}: {
  tri: FiltresOeuvres['tri'];
  onChange: (tri: Tri) => void;
}): JSX.Element {
  const { t } = useTranslation(['regie']);
  const tri = triCourant ?? 'recent';
  const { champ, sens } = decomposer(tri);
  const sensModifiable = CHAMPS[champ].decroissant !== null;
  const libelleSens = sensModifiable
    ? t(`regie:oeuvres.tri.sens.${sens === 'croissant' ? 'versDecroissant' : 'versCroissant'}`)
    : t('regie:oeuvres.tri.sens.fixe');

  function choisir(suivant: ChampTri): void {
    const { croissant, decroissant } = CHAMPS[suivant];
    // Le sens courant est gardé quand le nouveau critère le connaît ; sinon
    // le sens naturel : récent d'abord pour la date, croissant pour le reste.
    if (sens === 'decroissant' && decroissant) onChange(decroissant);
    else if (sens === 'croissant' && suivant !== 'date') onChange(croissant);
    else onChange(decroissant ?? croissant);
  }

  return (
    <div className={styles.tri}>
      <DropdownMenu
        align="start"
        trigger={
          <button
            type="button"
            className={styles.declencheur}
            aria-label={`${t('regie:oeuvres.tri.libelle')} : ${t(`regie:oeuvres.tri.${tri}`)}`}
          >
            <ArrowDownUp size={16} strokeWidth={1.5} aria-hidden />
            <span className={styles.declencheurTexte}>{t(`regie:oeuvres.tri.${tri}`)}</span>
          </button>
        }
        entries={[
          { type: 'label', label: t('regie:oeuvres.tri.libelle') },
          ...ORDRE_CHAMPS.map((code) => ({
            type: 'item' as const,
            item: {
              key: code,
              label: t(`regie:oeuvres.tri.champ.${code}`),
              icon:
                code === champ ? (
                  <Check aria-hidden />
                ) : (
                  <span aria-hidden style={{ width: 16, height: 16 }} />
                ),
              onSelect: () => choisir(code),
            },
          })),
        ]}
      />
      <button
        type="button"
        className={styles.sens}
        disabled={!sensModifiable}
        aria-label={libelleSens}
        title={libelleSens}
        onClick={() => {
          const { croissant, decroissant } = CHAMPS[champ];
          if (decroissant) onChange(sens === 'croissant' ? decroissant : croissant);
        }}
      >
        {sens === 'croissant' ? (
          <ArrowUp size={16} strokeWidth={1.5} aria-hidden />
        ) : (
          <ArrowDown size={16} strokeWidth={1.5} aria-hidden />
        )}
      </button>
    </div>
  );
}
